import { useEffect, useRef } from 'react'
import { useToast } from './toast'
import { API_ERROR_EVENT, type ApiClientErrorEventDetail } from '../api/client'

const DEDUP_WINDOW_MS = 2000

// 不 toast 的 code:各自有自己的 UI flow,toast 反而干扰
const SILENT_CODES: ReadonlySet<string> = new Set([
  'CAPTCHA_REQUIRED', // GlobalCaptchaGate 弹验证码
  'CHALLENGE_EXPIRED', // 验证码 control flow
  'CHALLENGE_NOT_FOUND', // 同上
  'IDEMPOTENCY_HIT', // 幂等命中 = 成功语义
])

function variantFor(status: number): 'warning' | 'error' {
  return status >= 500 ? 'error' : 'warning'
}

function durationFor(variant: 'warning' | 'error'): number {
  return variant === 'error' ? 5000 : 3500
}

export type GlobalErrorToastProps = {
  // 翻译函数。约定:命中返回翻译文案;未命中返回 key 原样(i18next 默认行为)。
  // shared 不直接依赖 react-i18next,由调用方注入(通常是 useTranslation().t)。
  translate: (key: string) => string
}

export function GlobalErrorToast({ translate }: GlobalErrorToastProps): null {
  const toast = useToast()
  const lastShown = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    const onErr = (ev: Event) => {
      const { detail } = ev as CustomEvent<ApiClientErrorEventDetail>
      if (!detail) return
      const { code, status, message, url } = detail

      console.warn('[api-error]', { code, status, message, url })

      if (SILENT_CODES.has(code)) return

      const now = Date.now()
      const dedupKey = code || '_unknown'
      const last = lastShown.current.get(dedupKey) ?? 0
      if (now - last < DEDUP_WINDOW_MS) return
      lastShown.current.set(dedupKey, now)

      const variant = variantFor(status)
      const isNetwork =
        status >= 500 || code === 'NETWORK_ERROR' || code === 'NON_JSON_RESPONSE'

      const key = `errors.${code}`
      const translated = translate(key)
      const text =
        translated !== key
          ? translated
          : isNetwork
            ? translate('errors._network')
            : translate('errors._unknown')

      toast.push(text, { variant, duration: durationFor(variant) })
    }

    window.addEventListener(API_ERROR_EVENT, onErr)
    return () => window.removeEventListener(API_ERROR_EVENT, onErr)
  }, [translate, toast])

  return null
}
